// components/Enhance.jsx
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import img from "../../assets/images/customization.png";

gsap.registerPlugin(ScrollTrigger);

const Enhance = () => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const tagRef = useRef(null);
  const titleRef = useRef(null);
  const textRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });

      tl.from(tagRef.current, {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: "power3.out",
      })
        .from(
          titleRef.current,
          { opacity: 0, y: 40, duration: 1, ease: "expo.out" },
          "-=0.4"
        )
        .from(
          textRef.current,
          { opacity: 0, y: 30, duration: 1, ease: "power2.out" },
          "-=0.6"
        )
        .from(
          cardsRef.current,
          { opacity: 0, y: 40, duration: 1, ease: "power2.out" },
          "-=0.4"
        );

      gsap.from(imageRef.current, {
        opacity: 0,
        scale: 1.05,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: imageRef.current,
          start: "top 85%",
          end: "top 50%",
          scrub: true,
        },
      });

      const imgEl = imageRef.current?.querySelector("img");
      if (imgEl && window.matchMedia("(hover: hover)").matches) {
        imgEl.addEventListener("mouseenter", () =>
          gsap.to(imgEl, { scale: 1.03, duration: 0.5, ease: "power2.out" })
        );
        imgEl.addEventListener("mouseleave", () =>
          gsap.to(imgEl, { scale: 1, duration: 0.6, ease: "power2.out" })
        );
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full px-4 py-16 md:py-20 lg:py-28 xl:py-32 flex flex-col lg:flex-row justify-center items-center gap-8 lg:gap-16 max-w-[1400px] mx-auto"
    >
      {/* Image */}
      <div
        ref={imageRef}
        className="w-full lg:w-1/2 h-[300px] sm:h-[350px] md:h-[400px] lg:h-[500px] xl:h-[600px] rounded-2xl overflow-hidden relative flex-shrink-0"
      >
        {/* ✅ lazy + async — this image is below the fold */}
        <img
          src={img}
          alt="Try our booth experience"
          loading="lazy"
          decoding="async"
          fetchpriority="low"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Text Content */}
      <div className="w-full lg:w-1/2 flex flex-col items-start justify-center">
        <p
          ref={tagRef}
          className="font-poppins text-[#76cfff] tracking-widest uppercase mb-4 text-sm md:text-base font-bold"
        >
          Interactive Experience
        </p>

        <h1
          ref={titleRef}
          className="font-poppins text-white uppercase font-semibold text-left text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mb-6 leading-tight"
        >
          Try It Before Your Event
        </h1>

        <p
          ref={textRef}
          className="font-poppins text-white/90 tracking-wide text-sm sm:text-base md:text-lg font-light leading-relaxed mb-10"
        >
          Get a real feel for your booth experience before the big day. Test
          our frames live and keep the results, or explore your device's vinyl
          customization to design the perfect look. Everything you see is
          exactly what you get — no surprises, just confidence.
        </p>

        {/* Cards */}
        <div ref={cardsRef} className="w-full flex flex-col sm:flex-row gap-4">
          {/* Card 1 — Try Now */}
          <Link
            to="/TryNow"
            className="group flex-1 bg-white/5 border border-white/10 hover:border-[#76cfff]/60 hover:bg-white/10 transition-all duration-300 rounded-2xl p-6 flex flex-col gap-3"
          >
            <div className="text-3xl">📸</div>
            <h3 className="font-poppins text-white font-semibold text-lg">
              Try Your Frame
            </h3>
            <p className="font-poppins text-white/60 text-sm font-light leading-relaxed">
              Test our photo booth frames live in your browser, snap a shot,
              and keep the result instantly.
            </p>
            <span className="mt-auto text-[#76cfff] text-sm font-semibold group-hover:translate-x-1 transition-transform duration-200 inline-block">
              Try Now →
            </span>
          </Link>

          {/* Card 2 — Device */}
          <Link
            to="/Device"
            className="group flex-1 bg-white/5 border border-white/10 hover:border-[#76cfff]/60 hover:bg-white/10 transition-all duration-300 rounded-2xl p-6 flex flex-col gap-3"
          >
            <div className="text-3xl">🎨</div>
            <h3 className="font-poppins text-white font-semibold text-lg">
              Customize Your Device
            </h3>
            <p className="font-poppins text-white/60 text-sm font-light leading-relaxed">
              Preview and design your booth's vinyl wrap. Pick colors, styles,
              and branding to match your event perfectly.
            </p>
            <span className="mt-auto text-[#76cfff] text-sm font-semibold group-hover:translate-x-1 transition-transform duration-200 inline-block">
              Explore →
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Enhance;